import { useEffect, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import type { NavigationType } from 'react-router'

import type { CatalogFilterSeam } from 'features/catalog/hooks/use-catalog-filter-hash/use-catalog-filter-seam'
import { useFilterStore } from 'features/filters/store'

// ---------------------------------------------------------------------------
// Private sub-hook of use-catalog-filter-hash. Owns the URL -> state half:
// loads a saved `?filters=` hash from the backend, or overlays the global v1
// filter store on a PUSH with no hash yet, through the seam's store. Returns
// whether the panel reflects the URL.
// ---------------------------------------------------------------------------

export function useCatalogFilterHydration(params: {
  hashFromUrl: string | null
  needsFetch: boolean
  navigationType: NavigationType
  projectId: string
  seam: CatalogFilterSeam
  lastWrittenHashRef: { current: string | null }
}): boolean {
  const {
    hashFromUrl,
    needsFetch,
    navigationType,
    projectId,
    seam,
    lastWrittenHashRef,
  } = params
  const { ready, panel } = seam

  const [hydrated, setHydrated] = useState(false)
  const appliedOverlayRef = useRef(false)
  const initialNavigationTypeRef = useRef(navigationType)
  const initialGlobalFiltersRef = useRef(useFilterStore.getState().filters)
  const initialHashFromUrlRef = useRef(hashFromUrl)

  // Whether this mount ever showed a real hash — "never had one" (the
  // overlay's own initial write) vs. "just LOST one" (e.g. the "Catalog"
  // breadcrumb, linking to the bare path). Set during render, not an effect.
  const hasSeenHashRef = useRef(!!hashFromUrl)
  if (hashFromUrl) hasSeenHashRef.current = true

  const { data: loadedHash } = useQuery(
    ['catalog-filter-hash', projectId, seam.mode, hashFromUrl],
    () => seam.load(projectId, hashFromUrl!),
    {
      enabled: ready && !!projectId && needsFetch,
      refetchOnWindowFocus: false,
    },
  )

  // Mount-only overlay (global v1 filters -> panel) on a PUSH with no hash of
  // Catalog's own. Runs exactly once (deferred until `ready`, so it writes
  // the right store) regardless of whether a hash was present at mount, so a
  // later navigation that drops the hash (Remove-last-filter, "Catalog"
  // breadcrumb) can't re-trigger it.
  useEffect(() => {
    if (!ready || appliedOverlayRef.current) return
    appliedOverlayRef.current = true
    if (initialHashFromUrlRef.current) return // hydration owns this mount instead
    if (initialNavigationTypeRef.current !== 'PUSH') return
    const firstFilter = initialGlobalFiltersRef.current[0]
    if (firstFilter) seam.overlay(firstFilter)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only pass, once `ready`; the seam's actions write through stable zustand stores.
  }, [ready])

  useEffect(() => {
    if (!ready) return
    if (!needsFetch) {
      if (hashFromUrl && hashFromUrl === lastWrittenHashRef.current) {
        setHydrated(true)
        return
      }
      if (!hashFromUrl && panel.filters.length > 0 && hasSeenHashRef.current) {
        // Has seen a real hash before (not the overlay's own initial write)
        // but the URL just lost it — clear to match rather than let the
        // writer silently restore the old filters onto the new URL.
        seam.clearFilters()
      }
      setHydrated(true)
      return
    }

    if (loadedHash === undefined) return // still fetching

    seam.hydrate(loadedHash, hashFromUrl!)
    lastWrittenHashRef.current = hashFromUrl
    setHydrated(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the seam's actions write through stable zustand stores; re-running per ready/hashFromUrl/loadedHash/needsFetch change is exactly the desired cycle.
  }, [ready, hashFromUrl, loadedHash, needsFetch])

  return hydrated
}
